import { AppInitialLoadQuery } from '@/graphql/generated/app-initial-load.generated';
import { SessionDocument, SessionQuery } from '@/graphql/generated/session.generated';
import { useLazyQuery, useMutation } from '@apollo/client';
import posthog from 'posthog-js';
import { Crisp } from 'crisp-sdk-web';
import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import dayjs from '@/libs/dayjs';
import productLift from '@/utils/product-lift';
import Cookies from 'js-cookie';
import { COOKIES } from '@/constants';
import { LogoutDocument } from '@/graphql/generated/logout.generated';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';
import { useAndieMemory } from './andie-memory-provider';
import {
    clearLoginRedirectGuard,
    broadcastLogout,
    AUTH_LOGOUT_BROADCAST_KEY,
} from '@/pages/public/auth/auth-utils';
import { isUsingComposeBeta } from '@/utils/beta/version-cohorts';
import { POSTHOG_ENABLED } from './posthog-provider';
import { toBcp47 } from '@/libs/i18n/settings';

const Context = createContext<{
    me: SessionQuery['me'];
    loading: boolean;
    refetch: (callback?: () => void) => void;
    workspace: SessionQuery['currentWorkspace'];
    logout: {
        logout: (options?: { redirectTo?: string }) => void;
        loading: boolean;
    };
} | null>(null);

export function SessionProvider({
    initialValue,
    children,
}: {
    initialValue: AppInitialLoadQuery;
    children: React.ReactNode;
}) {
    const [session, setSession] = useState(initialValue.me);
    const [workspace, setWorkspace] = useState(initialValue.currentWorkspace);
    const [getMe, { loading }] = useLazyQuery(SessionDocument, { fetchPolicy: 'network-only' });
    const { i18n } = useTranslation();
    const { pathname } = useLocation();
    const { setMemory } = useAndieMemory();

    // Centralize logout request
    const { enqueueSnackbar } = useSnackbar();
    // Where to land after the logout mutation resolves. Defaults to the homepage logout; the delete
    // flows override it to the account-deleted confirmation page. Held in a ref so onCompleted (a stable
    // closure) reads the value chosen by the caller.
    const logoutRedirectRef = useRef<string | null>(null);
    const [logoutMutation, { loading: loadingLogout }] = useMutation(LogoutDocument, {
        onCompleted: (data) => {
            if (data.logout) {
                posthog.reset();
                // Tell any other open Swonkie tab to log out too, then leave this one.
                broadcastLogout();
                const target =
                    logoutRedirectRef.current || `${import.meta.env.VITE_HOMEPAGE_ROOT}/logout`;
                logoutRedirectRef.current = null;
                window.location.href = target;
            } else {
                enqueueSnackbar(t('error.something-wrong'));
            }
        },
        onError: (e) => enqueueSnackbar(t(e.message)),
    });

    const logout = useCallback(
        (options?: { redirectTo?: string }) => {
            logoutRedirectRef.current = options?.redirectTo ?? null;
            void logoutMutation();
        },
        [logoutMutation],
    );

    // The app only mounts this provider once it has a live session, so a successful load means the
    // login page's redirect actually landed — clear its loop guard so a later revisit to /auth/login
    // isn't mistaken for a bounce.
    useEffect(() => {
        clearLoginRedirectGuard();
    }, []);

    // Cross-tab logout: react when another tab (or the account-deleted page) broadcasts a logout.
    useEffect(() => {
        const onStorage = (event: StorageEvent) => {
            if (event.key === AUTH_LOGOUT_BROADCAST_KEY && event.newValue) {
                window.location.href = `${import.meta.env.VITE_HOMEPAGE_ROOT}/logout`;
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    // update context when initial query updates (ex.: apollo store reset)
    useEffect(() => {
        setSession(initialValue.me);
        setWorkspace(initialValue.currentWorkspace);
    }, [initialValue.me, initialValue.currentWorkspace]);

    const fetch = useCallback(
        (callback = () => {}) => {
            getMe().then((data) => {
                if (data.data?.me) {
                    setSession(data.data.me);
                    setWorkspace(data.data.currentWorkspace);
                    callback();
                } else if (data.error?.graphQLErrors[0]?.extensions?.code !== 'FORBIDDEN') {
                    const errorMessage = data.error?.message || 'Unknown error';
                    throw new Error(`Session fetch failed unexpectedly: ${errorMessage}`);
                }
            });
        },
        [getMe],
    );

    useEffect(() => {
        // Set Sentry User
        Sentry.setUser({
            id: session.publicId,
            email: session.email,
        });

        // Set PostHog Identity
        const currentPHdistinctId = posthog.get_distinct_id();
        if (!isNaN(+currentPHdistinctId) && currentPHdistinctId !== session.publicId.toString()) {
            posthog.reset();
        }
        posthog.identify(session.publicId.toString(), {
            name: session.name,
            email: session.email,
        });
        posthog.group('workspace', workspace.publicId.toString(), {
            name: workspace.name,
            // Baseline for the legacy/fenix migration (DEV-4478). Without this we would only ever
            // see workspaces that switch, never how many are sitting on each version.
            createdAt: workspace.createdAt,
            composeVersion: isUsingComposeBeta(workspace) ? 'fenix' : 'legacy',
        });

        // Set Crisp User
        Crisp.user.setEmail(session.email);
        Crisp.user.setNickname(session.name || 'Unknown');
        Crisp.session.setData({
            userId: session.publicId,
            workspaceId: workspace.publicId,
            posthogSessionId: posthog.get_session_id(),
        });

        Crisp.user.setCompany(workspace.publicId.toString(), {
            description: workspace.name || '',
        });

        productLift.identify({
            uid: session.publicId.toString(),
            name: session.name || '',
            email: session.email,
        });

        setMemory({ userLanguage: session.language?.code || 'en' });

        // Change Language
        i18n.changeLanguage(session.language?.code || 'en');

        // PostHog picks survey translations from override_display_language > person property >
        // navigator.language. Without this it would fall back to the browser locale, which is not
        // the language the user chose in Swonkie - e.g. a pt user on an en-US browser gets English.
        if (POSTHOG_ENABLED) {
            posthog.set_config({
                override_display_language: toBcp47(session.language?.code || 'en'),
            });
        }

        dayjs.tz.setDefault(session.timezone?.timezone);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, i18n, workspace]);

    // Redirect after login based on cookie
    const redirectPath = Cookies.get(COOKIES.REDIRECT_AFTER_LOGIN);
    useEffect(() => {
        if (redirectPath) {
            // This function uses window.location instead of React Router hooks
            // to ensure support for the workspace path when a basename is configured in React Router.
            const fullPath = `${window.location.pathname}${window.location.search}`;
            // Check if the redirect path is the same as the current path
            if (!fullPath.startsWith(redirectPath)) {
                window.location.replace(redirectPath);
                Cookies.remove(COOKIES.REDIRECT_AFTER_LOGIN);
            }
        }
    }, [redirectPath, pathname]);

    const contextValue = useMemo(
        () => ({
            me: session,
            refetch: fetch,
            loading,
            workspace,
            logout: {
                logout,
                loading: loadingLogout,
            },
        }),
        [session, fetch, loading, workspace, logout, loadingLogout],
    );

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

export function useSession() {
    const currentContext = useContext(Context);
    if (!currentContext) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return currentContext;
}

/**
 * The session when there is one, and undefined when there isn't — for components that render on both
 * the authenticated app and a shared view.
 *
 * The public layout mounts no SessionProvider, so `useSession` throws there. That is the right
 * default for anything that genuinely needs an account; reach for this only where "nobody is signed
 * in" is a legitimate state rather than a bug.
 */
export function useOptionalSession() {
    return useContext(Context);
}
